// Notifications redux slice — overwrites the Plan 01 placeholder.
//
// State shape:
//   items[]      — last N notifications (DESC by createdAt), as DTOs from
//                  the API (NotificationDTO).
//   unreadCount  — kept independently because the SSE stream emits an
//                  initial unread-count event before any items load, and
//                  the polling fallback only refreshes this scalar.
//   loading      — for the initial fetch.
//   error        — last fetch/update error string (for UI toasts).
//
// Reducers:
//   addNotification(state, action)  — prepend SSE-pushed item, increment
//                                      unreadCount if !isRead.
//   setUnreadCount(state, count)    — replace count (polling fallback).
//
// Thunks (createAsyncThunk):
//   fetchList(limit?)               — GET /api/notifications?limit
//   fetchUnreadCount()              — GET /api/notifications/unread-count
//   markRead(ids[])                 — POST { action: 'mark-read', ids }
//   markAllRead()                   — POST { action: 'mark-all-read' }
//
// Plan 06 (UI wiring) is downstream of Plan 03; this slice is consumed by
// NotificationsBell + NotificationsBellDropdown in Task 3.

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'

import type { NotificationDTO } from '@/services/notifications/notification-stream.service'

interface NotificationsState {
  items: NotificationDTO[]
  unreadCount: number
  loading: boolean
  error: string | null
}

const initialState: NotificationsState = {
  items: [],
  unreadCount: 0,
  loading: false,
  error: null
}

// ============================================================
// THUNKS
// ============================================================

export const fetchList = createAsyncThunk('notifications/fetchList', async (limit: number = 20) => {
  const res = await fetch(`/api/notifications?limit=${encodeURIComponent(String(limit))}`)
  const json = await res.json()

  if (!json.success) {
    throw new Error(json.error ?? 'Не удалось загрузить уведомления')
  }

  return json.data as NotificationDTO[]
})

export const fetchUnreadCount = createAsyncThunk('notifications/fetchUnreadCount', async () => {
  const res = await fetch('/api/notifications/unread-count')
  const json = await res.json()

  if (!json.success) {
    throw new Error(json.error ?? 'Не удалось загрузить счётчик уведомлений')
  }

  return (json.data?.count as number) ?? 0
})

export const markRead = createAsyncThunk('notifications/markRead', async (ids: string[]) => {
  const res = await fetch('/api/notifications', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'mark-read', ids })
  })

  const json = await res.json()

  if (!json.success) {
    throw new Error(json.error ?? 'Не удалось отметить уведомление прочитанным')
  }

  return ids
})

export const markAllRead = createAsyncThunk('notifications/markAllRead', async () => {
  const res = await fetch('/api/notifications', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'mark-all-read' })
  })

  const json = await res.json()

  if (!json.success) {
    throw new Error(json.error ?? 'Не удалось отметить все уведомления прочитанными')
  }

  return true
})

// ============================================================
// SLICE
// ============================================================

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    addNotification: (state, action: PayloadAction<NotificationDTO>) => {
      const notif = action.payload

      // De-dupe by id (SSE could in theory replay during reconnect).
      if (state.items.some(item => item.id === notif.id)) return

      state.items.unshift(notif)

      // Trim to a sane upper bound to keep memory bounded.
      if (state.items.length > 100) {
        state.items.length = 100
      }

      if (!notif.isRead) {
        state.unreadCount += 1
      }
    },
    setUnreadCount: (state, action: PayloadAction<number>) => {
      state.unreadCount = Math.max(0, action.payload)
    },
    clearError: state => {
      state.error = null
    }
  },
  extraReducers: builder => {
    // fetchList
    builder.addCase(fetchList.pending, state => {
      state.loading = true
      state.error = null
    })
    builder.addCase(fetchList.fulfilled, (state, action) => {
      state.items = action.payload
      state.unreadCount = action.payload.filter(n => !n.isRead).length
      state.loading = false
    })
    builder.addCase(fetchList.rejected, (state, action) => {
      state.loading = false
      state.error = action.error.message ?? 'Не удалось загрузить уведомления'
    })

    // fetchUnreadCount
    builder.addCase(fetchUnreadCount.fulfilled, (state, action) => {
      state.unreadCount = action.payload
    })

    // markRead
    builder.addCase(markRead.fulfilled, (state, action) => {
      const ids = new Set(action.payload)
      let unreadDelta = 0

      state.items = state.items.map(n => {
        if (ids.has(n.id) && !n.isRead) {
          unreadDelta++

          return { ...n, isRead: true }
        }

        return n
      })

      state.unreadCount = Math.max(0, state.unreadCount - unreadDelta)
    })

    // markAllRead
    builder.addCase(markAllRead.fulfilled, state => {
      state.items = state.items.map(n => (n.isRead ? n : { ...n, isRead: true }))
      state.unreadCount = 0
    })
  }
})

export const { addNotification, setUnreadCount, clearError } = notificationsSlice.actions

export default notificationsSlice.reducer
